import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BrainCircuit, CheckCircle2, Loader2, AlertTriangle, ArrowDownLeft, ArrowUpRight,
  ChevronDown, ChevronUp, Shield, Zap, FileText, UploadCloud, Upload, Layers,
  Scale, XCircle, Edit3, Eye, TrendingUp, RefreshCw, History, Activity, PenLine,
} from "lucide-react";

import EditRecordModal from "./EditRecordModal";
import ManualAllocateModal from "./ManualAllocateModal";
import ReviewIntelligencePrompt from "./ReviewIntelligencePrompt";
import { ManualEntryModal } from "./ManualEntryModal";
import HistoryDrawer from "./HistoryDrawer";
import IntakeSummaryPanel, { FileResultsList } from "./IntakeSummaryPanel";
import IntakeStagesPanel from "./IntakeStagesPanel";
import DocumentArchive from "./DocumentArchive";
import PreviewSweepModal from "./PreviewSweepModal";
import UploadStatusTracker from "./UploadStatusTracker";
import TrustDialPill from "./TrustDialPill";
import {
  createBatch, uploadBatchFiles, addManualEntry, getBatch, getBatchRecords, runBatchDiagnostic,
  getRun, approveRecord, manualAllocateRecord, editRecord, recalculateRecord,
  getBatchDiagnostics, getBatchStatus,
  commitBatch, applyBatch, getArchivedRecords, diagnoseAllPending, getArchive,
  allocateAllPendingRulesOnly, rollbackSweep,
} from "./api";

/* ═══════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════ */
const STATUS_BADGES = {
  not_analysed:      { label: "Not Analysed",     bg: "bg-slate-500/15",   text: "text-slate-400" },
  safe:              { label: "Safe to Approve",  bg: "bg-emerald-500/15", text: "text-emerald-400" },
  needs_review:      { label: "Needs Review",     bg: "bg-amber-500/15",   text: "text-amber-400" },
  approved:          { label: "Approved",         bg: "bg-cyan-500/15",    text: "text-cyan-400" },
  committed:         { label: "Committed",        bg: "bg-purple-500/15",  text: "text-purple-400" },
  failed:            { label: "Failed",           bg: "bg-red-500/15",     text: "text-red-400" },
  // Currency conversions / Wise internal balance moves — kept for audit
  // but excluded from vault calculations and the dashboard.
  internal_transfer: { label: "Internal Transfer", bg: "bg-slate-700/30",  text: "text-slate-300" },
};

const VAULT_LABELS = {
  TAX_CUSTODY: "Tax Custody", SUPPLIER_OBLIGATION: "Supplier Obligation",
  CUSTOMER_TRUST: "Customer Trust", INVENTORY_CUSTODY: "Inventory Custody",
  OPERATING_CUSTODY: "Operating Custody", FREE_EQUITY: "Free Equity",
};

const SUPPORTED_FORMATS = ["PDF", "CSV", "XLSX", "JSON", "TSV", "TXT", "DOCX"];

/* ═══════════════════════════════════════════
   UPLOAD PANEL
   ═══════════════════════════════════════════ */
function UploadPanel({ loading, onFiles }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const onChange = (e) => { onFiles(Array.from(e.target.files || [])); if (inputRef.current) inputRef.current.value = ""; };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); onFiles(Array.from(e.dataTransfer.files || [])); };
  return (
    <div
      className={`rounded-xl border-2 border-dashed transition-all ${dragOver ? "border-cyan-400 bg-cyan-500/[0.06]" : "border-slate-700/50 bg-slate-900/30 hover:border-slate-600/50"} ${loading ? "opacity-60 pointer-events-none" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      data-testid="upload-panel"
    >
      <input ref={inputRef} type="file" multiple className="hidden" onChange={onChange}
        accept=".pdf,.csv,.xlsx,.xls,.json,.tsv,.txt,.docx" data-testid="upload-file-input" />
      <div className="p-6 flex items-center gap-5">
        <div className="w-14 h-14 rounded-xl bg-slate-800/60 border border-slate-700/40 flex items-center justify-center shrink-0">
          {loading ? <Loader2 className="w-7 h-7 text-cyan-400 animate-spin" /> : <UploadCloud className="w-7 h-7 text-slate-400" />}
        </div>
        <div className="flex-1">
          <h2 className="text-base font-barlow font-bold text-slate-200" data-testid="upload-panel-title">Upload Financial Files</h2>
          <p className="text-xs text-slate-500 mt-0.5">Bank statements, invoices, bills, spreadsheets, PDFs, and structured data files</p>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {SUPPORTED_FORMATS.map(fmt => (
              <Badge key={fmt} variant="outline" className="text-[9px] border-slate-700 text-slate-500 font-mono">{fmt}</Badge>
            ))}
          </div>
        </div>
        <Button onClick={() => inputRef.current?.click()} disabled={loading}
          className="bg-cyan-600 hover:bg-cyan-500 text-white gap-2 shrink-0" data-testid="upload-select-files-btn">
          <Upload className="w-4 h-4" /> {loading ? "Uploading..." : "Select Files"}
        </Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   BATCH SUMMARY
   ═══════════════════════════════════════════ */
function BatchSummary({ batchId, summary, fileResults }) {
  if (!batchId) return null;
  const shortId = batchId.slice(0, 8);
  return (
    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4" data-testid="batch-summary">
      <div className="flex items-center gap-3 flex-wrap">
        <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">Current batch</span>
          <span className="font-mono text-cyan-400" data-testid="batch-summary-id">{shortId}</span>
          <span className="text-slate-400" data-testid="batch-summary-files-succeeded">{summary.files_succeeded} file{summary.files_succeeded !== 1 ? "s" : ""} uploaded</span>
          <span className="text-slate-400" data-testid="batch-summary-records-parsed">{summary.records_parsed} records parsed</span>
          <span className="text-cyan-400 font-semibold" data-testid="batch-summary-records-pooled">{summary.records_normalized || summary.records_pooled || 0} records pooled</span>
          {summary.files_failed > 0 && <span className="text-red-400 text-xs" data-testid="batch-summary-files-failed">{summary.files_failed} file{summary.files_failed !== 1 ? "s" : ""} failed</span>}
        </div>
      </div>
      {fileResults && fileResults.length > 0 && (
        <div className="mt-3 space-y-1 text-xs">
          {fileResults.map((f) => (
            <div key={f.id} className="flex items-center gap-3 text-slate-400" data-testid={`file-result-${f.id}`}>
              <FileText className="w-3 h-3 text-slate-600" />
              <span className="text-slate-300">{f.filename}</span>
              <Badge className={`text-[9px] border-none ${f.status === "pooled" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{f.status}</Badge>
              <span>{f.records_parsed} parsed</span>
              <span className="text-slate-600">·</span>
              <span className="text-cyan-400">{f.records_pooled} pooled</span>
              {f.error && <span className="text-red-400/80 ml-2">{f.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   STATUS CARDS (batch-scoped, run-aware)
   ═══════════════════════════════════════════ */
function StatusCards({ counts }) {
  const cards = [
    { key: "pending",      label: "Pending",        value: counts.pending,      colour: "text-slate-300" },
    { key: "safe",         label: "Safe to Approve", value: counts.safe,         colour: "text-emerald-400" },
    { key: "review",       label: "Needs Review",   value: counts.review,       colour: "text-amber-400" },
    { key: "approved",     label: "Approved",       value: counts.approved,     colour: "text-cyan-400" },
    { key: "committed",    label: "Committed",      value: counts.committed,    colour: "text-purple-400" },
    { key: "not_analysed", label: "Not Analysed",   value: counts.not_analysed, colour: "text-slate-500" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="status-cards">
      {cards.map(c => (
        <Card key={c.key} className="bg-slate-900/40 border-slate-800/50" data-testid={`status-card-${c.key}`}>
          <CardContent className="p-3 text-center">
            <div className="text-[9px] text-slate-500 uppercase tracking-wider">{c.label}</div>
            <div className={`text-2xl font-mono tabular font-bold mt-1 ${c.colour}`} data-testid={`status-card-${c.key}-value`}>{c.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════
   DIAGNOSTIC PANEL
   ═══════════════════════════════════════════ */
function DiagnosticPanel({ run, onApproveAllSafe, onReviewUncertain, onRetry, canApproveSafe, canReview }) {
  if (!run) return null;
  const state = run.run_status;
  return (
    <div className={`rounded-xl border ${state === "completed" ? "border-cyan-500/20 bg-cyan-500/[0.04]" : state === "partial_failure" ? "border-amber-500/20 bg-amber-500/[0.04]" : state === "failed" ? "border-red-500/20 bg-red-500/[0.04]" : "border-slate-700/30 bg-slate-900/30"} p-4`}
      data-testid="diagnostic-panel">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${state === "completed" ? "bg-cyan-500/15 text-cyan-400" : state === "partial_failure" ? "bg-amber-500/15 text-amber-400" : state === "failed" ? "bg-red-500/15 text-red-400" : "bg-slate-700/30 text-slate-400"}`}>
          {state === "completed" ? <BrainCircuit className="w-5 h-5" /> : state === "failed" ? <XCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-sm font-barlow font-bold text-slate-200" data-testid="diagnostic-run-state">
                {state === "completed" ? "Diagnostic completed" : state === "partial_failure" ? "Diagnostic partially completed" : state === "failed" ? "Diagnostic failed" : "Diagnostic running"}
              </div>
              <div className="text-[10px] text-slate-500 font-mono">Run {run.id?.slice(0, 8)}</div>
            </div>
            <div className="flex items-center gap-2">
              {canApproveSafe && (
                <Button onClick={onApproveAllSafe} size="sm" className="bg-emerald-600 hover:bg-emerald-500 gap-1.5 text-xs" data-testid="approve-all-safe-btn">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve Safe ({run.records_safe})
                </Button>
              )}
              {canReview && (
                <Button onClick={onReviewUncertain} size="sm" variant="outline" className="border-amber-500/30 text-amber-400 gap-1.5 text-xs" data-testid="review-uncertain-btn">
                  <Eye className="w-3.5 h-3.5" /> Review Uncertain ({run.records_needs_review})
                </Button>
              )}
              {(state === "failed" || state === "partial_failure") && (
                <Button onClick={onRetry} size="sm" variant="outline" className="border-red-500/30 text-red-400 gap-1.5 text-xs" data-testid="retry-diagnostic-btn">
                  <RefreshCw className="w-3.5 h-3.5" /> Retry
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2">{run.message}</p>
          {(state === "failed" || state === "partial_failure") && (
            <div className="mt-3 rounded-md bg-red-500/[0.04] border border-red-500/20 px-3 py-2" data-testid="diagnostic-failure-note">
              <div className="text-xs font-semibold text-red-300">
                {state === "failed" ? "AI Diagnostic Could Not Be Completed" : "AI Diagnostic Only Partially Completed"}
              </div>
              <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                The batch remains intact. No financial truth was applied. Your dashboard still reflects the last valid applied truth.
              </p>
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <StatMini label="Total" value={run.records_total} />
            <StatMini label="Rule-classified" value={run.records_rule_classified} colour="text-emerald-400" />
            <StatMini label="AI-classified" value={run.records_ai_classified} colour="text-cyan-400" />
            <StatMini label="Safe" value={run.records_safe} colour="text-emerald-400" />
            <StatMini label="Needs review" value={run.records_needs_review} colour="text-amber-400" />
            <StatMini label="Failed" value={run.records_failed} colour="text-red-400" />
            <StatMini label="AI retries" value={run.batch_retries} colour="text-slate-500" />
          </div>
        </div>
      </div>
    </div>
  );
}
function StatMini({ label, value, colour = "text-slate-300" }) {
  return (
    <div className="px-3 py-2 rounded-md bg-slate-800/30">
      <div className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`font-mono font-bold text-sm mt-0.5 ${colour}`}>{value ?? 0}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   RECORD CARD
   ═══════════════════════════════════════════ */
function RecordCard({ record, onEdit, onApprove, onAllocate, onRecalculate, onHistory, formatMoney }) {
  const [open, setOpen] = useState(false);
  const np = record.normalized_payload || {};
  const ai = record.ai_result;
  const allocs = record.final_allocation || ai?.recommended_vault_allocations || [];
  const badge = STATUS_BADGES[record.status] || STATUS_BADGES.not_analysed;
  const isInflow = np.direction === "inflow";
  const committed = record.status === "committed";

  return (
    <div className={`rounded-xl border transition-all ${record.status === "needs_review" ? "border-amber-500/20 bg-amber-500/[0.03]" : record.status === "safe" ? "border-emerald-500/20 bg-emerald-500/[0.03]" : record.status === "approved" ? "border-cyan-500/20 bg-cyan-500/[0.03]" : committed ? "border-purple-500/20 bg-purple-500/[0.03]" : "border-slate-800/40 bg-slate-900/30"}`}
      data-testid={`record-card-${record.id}`}>
      <div className="p-4 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`text-[9px] ${badge.bg} ${badge.text} border-none`} data-testid={`record-status-${record.id}`}>{badge.label}</Badge>
            <Badge className={`text-[9px] ${isInflow ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"} border-none gap-0.5`}>
              {isInflow ? <ArrowDownLeft className="w-2.5 h-2.5" /> : <ArrowUpRight className="w-2.5 h-2.5" />}
              {isInflow ? "Inflow" : "Outflow"}
            </Badge>
            {record.intake_status === "needs_intake_review" && (
              <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-none" data-testid={`record-intake-review-${record.id}`}>
                Needs Intake Review
              </Badge>
            )}
            {record.manual_override && <Badge className="text-[9px] bg-orange-500/15 text-orange-400 border-none">EDITED</Badge>}
            {record.source_type === "manual_entry" && (
              <Badge className="text-[9px] bg-cyan-500/15 text-cyan-300 border-none gap-1" data-testid={`record-source-manual-${record.id}`}>
                <PenLine className="w-2.5 h-2.5" /> Manual Entry
              </Badge>
            )}
            {/* Visual distinction for low-confidence direction-fallback allocations */}
            {ai?.classification_source === "direction_fallback" && (
              <Badge
                className="text-[9px] bg-amber-500/20 text-amber-200 border border-amber-500/40 gap-1"
                data-testid={`record-fallback-${record.id}`}
                title="No keyword matched — vault was guessed from inflow/outflow direction. Confidence is low; review before approving."
              >
                ⚠ Guess
              </Badge>
            )}
            {/* Sticky manual lock — overrides every other badge.
                Locked records are skipped by all sweeps and external syncs. */}
            {record.allocation_locked && (
              <Badge
                className="text-[9px] bg-slate-200/10 text-slate-100 border border-slate-300/40 gap-1 font-bold"
                data-testid={`record-locked-${record.id}`}
                title={
                  `🔒 Manually locked by ${record.locked_by || 'user'} on ` +
                  `${record.locked_at ? new Date(record.locked_at).toLocaleString('en-GB') : '—'}\n\n` +
                  `Reason: ${record.lock_reason || '(no reason recorded)'}\n\n` +
                  `This record will NOT be re-classified by AI sweeps, rules-only sweeps, or external syncs. ` +
                  `Use the Unlock action to remove this protection.`
                }
              >
                🔒 Manual
              </Badge>
            )}
            {ai?.classification_source === "rules" && (
              <Badge
                className="text-[9px] bg-emerald-500/[0.08] text-emerald-300 border border-emerald-500/30 gap-1"
                data-testid={`record-rules-${record.id}`}
                title="Keyword matched — high-confidence deterministic allocation."
              >
                ✓ Rules
              </Badge>
            )}
            {ai?.classification_source === "ai" && (
              <Badge
                className="text-[9px] bg-cyan-500/[0.08] text-cyan-300 border border-cyan-500/30 gap-1"
                data-testid={`record-ai-${record.id}`}
                title="Classified by GPT-5.2 AI."
              >
                ✓ AI
              </Badge>
            )}
            {ai?.classification_source === "ai_fallback_rules" && (
              <Badge
                className="text-[9px] bg-amber-500/[0.08] text-amber-300 border border-amber-500/30 gap-1"
                data-testid={`record-ai-fallback-${record.id}`}
                title="AI was unavailable for this record — rules engine fallback used."
              >
                ⚡ AI Fallback
              </Badge>
            )}
          </div>
          {/* Stored rationale — the explanation captured at the moment
              of classification. Stored, not regenerated, so it cannot
              drift from the decision that was actually made. */}
          {record.rationale?.summary && (
            <p className="mt-1.5 text-[11px] text-slate-400 leading-snug" data-testid={`record-rationale-${record.id}`}>
              <span className="text-slate-600">Why:</span> {record.rationale.summary}
            </p>
          )}
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <span className={`text-lg font-mono tabular font-bold ${isInflow ? "text-emerald-400" : "text-red-400"}`}>
              {isInflow ? "+" : "-"}{np.source_currency} {(np.source_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            {np.source_currency !== np.reporting_currency && (
              <span className="text-xs text-slate-500 font-mono">
                ≈ {np.reporting_currency} {(np.reporting_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                <span className="text-[9px] text-slate-600 ml-1">FX {np.fx_rate?.toFixed(4)}</span>
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-slate-200 font-semibold" data-testid={`record-description-${record.id}`}>
            {np.description || "No description"}
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <span>{np.counterparty || "Unknown counterparty"}</span>
            {np.date && <span>{String(np.date).slice(0, 10)}</span>}
            {np.reference && <span className="font-mono">{np.reference}</span>}
          </div>
          {/* Trust Dial — inline confidence band + parser source +
              balance status + top reasons. Pure render from
              record.trust_dial; no extra API calls. */}
          <TrustDialPill trustDial={record.trust_dial} recordId={record.id} />
        </div>
        {ai && (
          <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 shrink-0"
            data-testid={`record-expand-${record.id}`}>
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {open ? "Collapse" : "AI Detail"}
          </button>
        )}
      </div>

      {ai && (
        <div className="px-4 pb-3 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Classification</span>
            <span className="text-xs font-semibold text-slate-200" data-testid={`record-kind-${record.id}`}>{ai.recommended_kind}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Source</span>
            <span className="text-xs text-cyan-300 font-mono">{ai.classification_source}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Confidence</span>
            <span className={`text-xs font-mono font-bold ${ai.confidence >= 0.85 ? "text-emerald-400" : ai.confidence >= 0.70 ? "text-amber-400" : "text-red-400"}`}
              data-testid={`record-confidence-${record.id}`}>
              {(ai.confidence * 100).toFixed(0)}%
            </span>
          </div>
        </div>
      )}

      {open && ai && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-800/20 pt-3">
          {/* Intake block — extraction confidence + notes */}
          {(record.extraction_confidence != null || (record.extraction_notes && record.extraction_notes.length > 0)) && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                <FileText className="w-3 h-3" /> Intake extraction
              </div>
              <div className="px-3 py-2 rounded-lg bg-slate-800/20 border border-slate-700/20 text-xs">
                <div className="flex items-center gap-3 flex-wrap">
                  {record.extraction_confidence != null && (
                    <span className="text-slate-400">
                      confidence{" "}
                      <span className={`font-mono font-bold ${
                        record.extraction_confidence >= 0.85 ? "text-emerald-400" :
                        record.extraction_confidence >= 0.75 ? "text-cyan-400" :
                        "text-amber-400"
                      }`} data-testid={`record-extraction-conf-${record.id}`}>
                        {(record.extraction_confidence * 100).toFixed(0)}%
                      </span>
                    </span>
                  )}
                  {record.intake_status && (
                    <span className="text-slate-400">
                      intake <span className={`font-mono ${
                        record.intake_status === "normalized" ? "text-emerald-400" :
                        record.intake_status === "needs_intake_review" ? "text-amber-400" :
                        "text-red-400"
                      }`}>{record.intake_status}</span>
                    </span>
                  )}
                </div>
                {record.extraction_notes && record.extraction_notes.length > 0 && (
                  <ul className="mt-1 space-y-0.5" data-testid={`record-extraction-notes-${record.id}`}>
                    {record.extraction_notes.map((n, i) => (
                      <li key={i} className="text-[11px] text-amber-300/80 flex items-start gap-1.5">
                        <span className="text-slate-600">•</span><span>{n}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          {allocs.length > 0 && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1"><Layers className="w-3 h-3" /> Vault allocation</div>
              <div className="space-y-1">
                {allocs.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-xs px-3 py-1.5 rounded-lg bg-slate-800/30">
                    <span className="text-slate-300">{VAULT_LABELS[a.vault] || a.vault}</span>
                    <span className="font-mono font-semibold text-emerald-400">
                      {a.reporting_currency} {(a.reporting_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {ai.bubble_action?.action && ai.bubble_action.action !== "none" && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Scale className="w-3 h-3" /> Bubble action</div>
              <div className="text-xs text-cyan-400 px-3 py-1.5 rounded-lg bg-cyan-500/5 border border-cyan-500/10">
                {ai.bubble_action.action} {ai.bubble_action.bubble_type || ""} — {ai.bubble_action.reason_code?.replace(/_/g, " ")}
              </div>
            </div>
          )}
          {ai.obligation_actions?.length > 0 && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Shield className="w-3 h-3" /> Obligations</div>
              {ai.obligation_actions.map((o, i) => (
                <div key={i} className="text-xs text-amber-400 px-3 py-1.5 rounded-lg bg-amber-500/5 border border-amber-500/10 mb-1">
                  {o.action} {o.obligation_type} — {o.claimant}
                </div>
              ))}
            </div>
          )}
          {ai.reasoning?.length > 0 && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><BrainCircuit className="w-3 h-3" /> AI reasoning</div>
              <div className="px-3 py-2 rounded-lg bg-slate-800/20 border border-slate-700/20 space-y-1">
                {ai.reasoning.map((r, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="text-slate-600 mt-0.5">•</span>
                    <span className="text-slate-400"><span className="text-slate-300 font-medium">{r.factor}:</span> {r.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
        {!committed && (
          <>
            <Button size="sm" variant="outline" className="h-7 text-xs border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 gap-1"
              onClick={() => onEdit(record)} data-testid={`edit-btn-${record.id}`}>
              <Edit3 className="w-3 h-3" /> Edit
            </Button>
            {ai && record.status !== "approved" && (
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 gap-1"
                onClick={() => onApprove(record.id)} data-testid={`approve-btn-${record.id}`}>
                <CheckCircle2 className="w-3 h-3" /> Approve
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7 text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10 gap-1"
              onClick={() => onAllocate(record)} data-testid={`allocate-btn-${record.id}`}>
              <Scale className="w-3 h-3" /> Manual Allocate
            </Button>
            {ai && (
              <Button size="sm" variant="outline" className="h-7 text-xs border-purple-500/30 text-purple-400 hover:bg-purple-500/10 gap-1"
                onClick={() => onRecalculate(record.id)} data-testid={`recalc-btn-${record.id}`}>
                <RefreshCw className="w-3 h-3" /> Recalculate
              </Button>
            )}
          </>
        )}
        <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 hover:text-slate-300 gap-1"
          onClick={() => onHistory(record.id)} data-testid={`history-btn-${record.id}`}>
          <History className="w-3 h-3" /> History
        </Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   APPLY RESULT PANEL
   ═══════════════════════════════════════════ */
function ApplyResultPanel({ result, onDismiss, onGoToDashboard }) {
  if (!result) return null;
  const vt = result.vault_totals || {};
  const fr = result.financial_reality || {};
  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-5" data-testid="apply-result-panel">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-barlow font-bold text-amber-400">Dashboard Truth Updated</h3>
          {fr.headline && <p className="text-sm text-slate-200 mt-1 font-semibold" data-testid="apply-headline">{fr.headline}</p>}
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
            <span>Items applied: <span className="text-cyan-400 font-mono" data-testid="apply-items">{result.committed_items_applied}</span></span>
            <span>Ledger entries: <span className="text-cyan-400 font-mono" data-testid="apply-ledger">{result.ledger_entries_created}</span></span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={onGoToDashboard} size="sm" className="bg-cyan-600 hover:bg-cyan-500 text-xs" data-testid="goto-dashboard-btn">
            Open Dashboard
          </Button>
          <Button onClick={onDismiss} size="sm" variant="ghost" className="text-xs text-slate-500">Dismiss</Button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
        {Object.entries(vt).map(([k, v]) => (
          <div key={k} className="flex justify-between text-xs px-3 py-1.5 rounded-lg bg-slate-800/20">
            <span className="text-slate-400">{VAULT_LABELS[k] || k}</span>
            <span className="text-slate-200 font-mono font-semibold">{v.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════ */
export default function DataPoolPage() {
  const { formatMoney } = useAuth();
  const navigate = useNavigate();
  const recordsRef = useRef(null);
  const pollRef = useRef(null);

  // Persisted-current-batch key — scoped per logged-in user so two users
  // on the same browser don't collide.
  const userKey = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("sv_user") || "{}");
      return u.id || u.email || "anon";
    } catch { return "anon"; }
  })();
  const BATCH_KEY = `sv_current_batch_${userKey}`;

  // Strict state contract — see types.js
  const [currentBatchId, setCurrentBatchId] = useState(() => localStorage.getItem(BATCH_KEY) || null);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [currentScope, setCurrentScope] = useState("pending");
  const [runStatus, setRunStatus] = useState("idle");
  const [batchSummary, setBatchSummary] = useState(null);
  const [fileResults, setFileResults] = useState([]);
  const [runSummary, setRunSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [manualEntrySubmitting, setManualEntrySubmitting] = useState(false);
  // Intake state machine — isolated from later diagnostic state
  const [intakeState, setIntakeState] = useState("idle"); // idle|uploading|processing|completed|completed_with_issues|review_required|failed
  const [intakeMetrics, setIntakeMetrics] = useState({
    files_attempted: 0, files_succeeded: 0, files_failed: 0,
    records_extracted: 0, records_normalized: 0, records_flagged_for_review: 0,
  });
  // Pipeline visibility — populated by /diagnostics whenever the
  // pipeline lands in review_required or failed. Without this the
  // user sees a perpetual spinner with no explanation.
  const [pipelineMessage, setPipelineMessage] = useState("");
  const [fileErrors, setFileErrors] = useState([]);
  const pipelinePollRef = useRef(null);

  // modal state
  const [editItem, setEditItem] = useState(null);
  const [allocItem, setAllocItem] = useState(null);
  const [historyItemId, setHistoryItemId] = useState(null);
  // P3 — Review Intelligence prompt (approve / recalculate)
  const [reviewPromptAction, setReviewPromptAction] = useState(null); // "approve" | "recalculate"
  const [reviewPromptRecord, setReviewPromptRecord] = useState(null);

  // commit/apply state
  const [committing, setCommitting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  /* ── Refresh records for current batch (strictly scoped) ── */
  const refreshRecords = useCallback(async (overrideBatchId) => {
    const bid = overrideBatchId ?? currentBatchId;
    if (!bid) return;
    try {
      const res = await getBatchRecords(bid);
      const list = res.data.records;
      // STRICT: never clear records on a malformed payload. Only commit
      // a real array. This guarantees the Data Pool view never goes
      // blank during or after a diagnostic run.
      if (Array.isArray(list)) {
        setRecords(list);
      }
    } catch (e) {
      // Transient backend slowness while a long diagnostic is running
      // must NOT wipe the existing records list. Surface a non-blocking
      // error and leave records intact.
      setError(e.response?.data?.detail?.message || "Failed to refresh records");
    }
  }, [currentBatchId]);

  /* ── Persist current batch id + auto-restore records on mount ── */
  useEffect(() => {
    if (currentBatchId) {
      localStorage.setItem(BATCH_KEY, currentBatchId);
    } else {
      localStorage.removeItem(BATCH_KEY);
    }
  }, [currentBatchId, BATCH_KEY]);

  // On first mount, if we have a stored batch but no records loaded yet,
  // fetch them via the archive endpoint (always available, never affected
  // by transient diagnostic state).
  useEffect(() => {
    const stored = localStorage.getItem(BATCH_KEY);
    if (!stored) return;
    (async () => {
      try {
        const [batchRes, recsRes, statusRes] = await Promise.all([
          getBatch(stored).catch(() => null),
          getArchivedRecords(stored).catch(() => null),
          // P0 Feb-2026 — pull file_results so the parser cascade UI
          // populates immediately on page reload, not only after a
          // fresh upload event.
          getBatchStatus(stored).catch(() => null),
        ]);
        if (batchRes?.data?.batch) setBatchSummary(batchRes.data.batch);
        const list = recsRes?.data?.records;
        if (Array.isArray(list) && list.length > 0) setRecords(list);
        const sd = statusRes?.data;
        if (Array.isArray(sd?.file_results) && sd.file_results.length) {
          setFileResults(sd.file_results);
          // Sync intakeState so the FileResultsList renders.
          if (sd.pipeline_status === "review_required") setIntakeState("review_required");
          else if (sd.pipeline_status === "failed") setIntakeState("failed");
          else if (["applied","completed","allocated_and_applied"].includes(sd.pipeline_status))
            setIntakeState((sd.records_flagged_for_review || 0) > 0 ? "completed_with_issues" : "completed");
        }
      } catch { /* never wipe on rehydrate failure */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Poll diagnostic run ── */
  const startRunPolling = useCallback((runId, batchId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let consecutiveErrors = 0;
    pollRef.current = setInterval(async () => {
      try {
        const res = await getRun(runId);
        consecutiveErrors = 0;
        const run = res.data.run;
        setRunSummary(run);
        setRunStatus(run.run_status);
        if (run.run_status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          await refreshRecords(batchId);
        }
      } catch (e) {
        // Transient timeouts during heavy AI batches are expected — the
        // backend may be momentarily slow but the run is still alive.
        // Only mark failed after 5 consecutive errors (~12.5s at 2.5s
        // interval). Critically, NEVER touch the records list here.
        consecutiveErrors += 1;
        if (consecutiveErrors >= 5) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunStatus("failed");
        }
      }
    }, 2500);
  }, [refreshRecords]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  /* ── Handlers ── */
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setLoading(true);
    setError(null);
    // STRICT RESET: every upload starts a completely fresh batch context
    setBatchSummary(null);
    setFileResults([]);
    setRunSummary(null);
    setRunStatus("idle");
    setCurrentRunId(null);
    setCurrentBatchId(null);
    setRecords([]);
    setApplyResult(null);
    setCurrentScope("pending");
    // Intake state → uploading
    setIntakeState("uploading");
    setIntakeMetrics({ files_attempted: files.length, files_succeeded: 0, files_failed: 0,
      records_extracted: 0, records_normalized: 0, records_flagged_for_review: 0 });
    try {
      const batchRes = await createBatch();
      const batchId = batchRes.data.batch.id;
      setCurrentBatchId(batchId);

      // Intake state → processing
      setIntakeState("processing");

      const uploadRes = await uploadBatchFiles(batchId, files);
      const data = uploadRes.data;
      // The new staged pipeline returns only `files_received` and
      // `pipeline_status: "uploaded"` synchronously — the heavy lifting
      // happens in the background. The UploadStatusTracker component
      // polls /status for progress.
      setFileResults(data.file_results || []);
      setIntakeMetrics({
        files_attempted: data.files_received ?? data.files_attempted ?? files.length,
        files_succeeded: data.files_succeeded ?? (data.files_received || 0),
        files_failed: data.files_failed ?? 0,
        records_extracted: data.records_extracted ?? 0,
        records_normalized: data.records_normalized ?? 0,
        records_flagged_for_review: data.records_flagged_for_review ?? 0,
      });
      setIntakeState(data.intake_state || "processing");

      const bres = await getBatch(batchId);
      setBatchSummary(bres.data.batch);
      await refreshRecords(batchId);

      // ── Pipeline-status poller ──────────────────────────────
      // The upload endpoint returns synchronously with intake_state
      // ``processing``. The real pipeline (detect → parse → classify
      // → allocate → apply) runs in the background and writes its
      // verdict to ``upload_batches.pipeline_status``. Without this
      // poller the user sees a perpetual spinner when the pipeline
      // lands in ``review_required`` because no front-end signal ever
      // arrived to flip the panel to a terminal state. We now poll
      // every 2 s for up to 60 s, then fetch /diagnostics to render
      // the exact per-file reason in the IntakeSummaryPanel.
      if (pipelinePollRef.current) clearInterval(pipelinePollRef.current);
      const startedAt = Date.now();
      pipelinePollRef.current = setInterval(async () => {
        try {
          const fresh = await getBatch(batchId);
          const b = fresh.data.batch;
          if (b) setBatchSummary(b);
          const pStatus = b?.pipeline_status;
          const TERMINAL = new Set([
            "allocated_and_applied", "applied", "completed",
            "review_required", "failed",
          ]);
          if (pStatus && TERMINAL.has(pStatus)) {
            clearInterval(pipelinePollRef.current);
            pipelinePollRef.current = null;
            // Map backend pipeline_status onto the panel's intake_state.
            if (pStatus === "review_required") {
              setIntakeState("review_required");
            } else if (pStatus === "failed") {
              setIntakeState("failed");
            } else {
              // success path — keep panel as completed/with_issues
              const issues = (b?.records_flagged_for_review || 0) > 0;
              setIntakeState(issues ? "completed_with_issues" : "completed");
            }
            // P0 Feb-2026 — pick up file_results from /status so the
            // parser cascade UI populates even when the user
            // navigates back to a previously-uploaded batch (not just
            // the initial upload response).
            if (Array.isArray(b?.file_results) && b.file_results.length) {
              setFileResults(b.file_results);
            }
            // Fetch diagnostics — populates pipelineMessage + fileErrors
            try {
              const diag = await getBatchDiagnostics(batchId);
              setPipelineMessage(diag.data.pipeline_message || "");
              setFileErrors(diag.data.file_processing_errors || []);
            } catch (_) { /* visibility nicety, never blocks */ }
            await refreshRecords(batchId);
          } else if (Date.now() - startedAt > 60_000) {
            clearInterval(pipelinePollRef.current);
            pipelinePollRef.current = null;
          }
        } catch (_) { /* swallow — let the next tick retry */ }
      }, 2000);
    } catch (e) {
      setIntakeState("failed");
      setError(e.response?.data?.detail?.message || e.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRunDiagnostic = async () => {
    if (!currentBatchId) return;
    setRunStatus("running");
    setRunSummary(null);
    try {
      const res = await runBatchDiagnostic(currentBatchId);
      const runId = res.data.run_id;
      setCurrentRunId(runId);
      startRunPolling(runId, currentBatchId);
    } catch (e) {
      setRunStatus("failed");
      setError(e.response?.data?.detail?.message || "Diagnostic failed to start");
    }
  };

  /* ── Org-level sweep — analyse every batch that still has pending records.
     This is the fix for "I uploaded 512 records and pressed Run AI Diagnostic
     but only the active batch got classified". A single click runs the
     diagnostic across every batch with not_analysed/failed records. */
  const [allPendingCount, setAllPendingCount] = useState(0);
  const [allPendingBatches, setAllPendingBatches] = useState(0);
  const [sweepRunning, setSweepRunning] = useState(false);

  const checkAllPending = useCallback(async () => {
    try {
      const res = await getArchive();
      const list = res.data.archive || [];
      // A batch counts as "pending" if it has records but isn't fully committed yet.
      const pending = list.filter((b) => b.record_count > 0 && b.ledger_count < b.record_count);
      setAllPendingBatches(pending.length);
      setAllPendingCount(pending.reduce((sum, b) => sum + (b.record_count - (b.ledger_count || 0)), 0));
    } catch { /* leave counters unchanged on transient error */ }
  }, []);

  useEffect(() => { checkAllPending(); }, [checkAllPending, runStatus, applyResult]);

  const handleRunAllPending = async () => {
    setSweepRunning(true);
    setError(null);
    try {
      const res = await diagnoseAllPending();
      const data = res.data;
      // After kick-off, poll every 2.5s up to 3 minutes; refresh counts when done.
      const runIds = (data.runs || []).map((r) => r.run_id);
      if (runIds.length === 0) {
        setSweepRunning(false);
        await checkAllPending();
        return;
      }
      const deadline = Date.now() + 3 * 60 * 1000;
      const tick = async () => {
        if (Date.now() > deadline) { setSweepRunning(false); await checkAllPending(); return; }
        try {
          const statuses = await Promise.all(
            runIds.map((rid) => getRun(rid).then((r) => r.data.run.run_status).catch(() => "running"))
          );
          const allDone = statuses.every((s) => s !== "running");
          if (allDone) {
            setSweepRunning(false);
            await checkAllPending();
            // Refresh active-batch records too so the table reflects new statuses
            if (currentBatchId) await refreshRecords(currentBatchId);
            return;
          }
        } catch { /* keep polling */ }
        setTimeout(tick, 2500);
      };
      setTimeout(tick, 2500);
    } catch (e) {
      setSweepRunning(false);
      setError(e.response?.data?.detail || "Failed to start org-wide diagnostic");
    }
  };

  /* ── Auto-Allocate (No AI) — same scope as the AI sweep but uses only
     the deterministic rules engine. Synchronous, instant, never fails.
     UX flow: button → dry-run preview modal → user confirms → write →
     show rollback toast for 30s. */
  const [rulesOnlyRunning, setRulesOnlyRunning] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [lastSweep, setLastSweep] = useState(null); // {sweep_id, allocated, review, expires_at}

  const handleOpenPreview = () => setPreviewOpen(true);

  const handleConfirmAutoAllocate = async () => {
    setPreviewOpen(false);
    setRulesOnlyRunning(true);
    setError(null);
    try {
      const res = await allocateAllPendingRulesOnly();
      const data = res.data;
      // Track this sweep so the user can roll it back
      const expiresAt = Date.now() + 30 * 60 * 1000; // 30 minute rollback window
      const reviewCount = (data.results || []).reduce((s, r) => s + (r.needs_review || 0), 0);
      setLastSweep({
        sweep_id: data.sweep_id,
        allocated: data.records_allocated,
        review: reviewCount,
        expires_at: expiresAt,
      });
      await checkAllPending();
      if (currentBatchId) await refreshRecords(currentBatchId);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to auto-allocate pending records");
    } finally {
      setRulesOnlyRunning(false);
    }
  };

  const handleRollbackLastSweep = async () => {
    if (!lastSweep?.sweep_id) return;
    setRulesOnlyRunning(true);
    try {
      await rollbackSweep(lastSweep.sweep_id);
      setLastSweep(null);
      await checkAllPending();
      if (currentBatchId) await refreshRecords(currentBatchId);
    } catch (e) {
      setError(e.response?.data?.detail || "Rollback failed");
    } finally {
      setRulesOnlyRunning(false);
    }
  };

  const handleApprove = (recordId) => {
    // P3 — open the Review Intelligence prompt instead of approving silently.
    const rec = records.find((r) => r.id === recordId) || { id: recordId };
    setReviewPromptRecord(rec);
    setReviewPromptAction("approve");
  };

  /** Called from the ReviewIntelligencePrompt after the user picks
   *  reason_code + user_confidence (+ optional note). Performs the
   *  actual backend action. */
  const handleReviewConfirm = async (review) => {
    const rec = reviewPromptRecord;
    const action = reviewPromptAction;
    if (!rec || !action) return;
    if (action === "approve") {
      await approveRecord(rec.id, review).catch(() => {});
    } else if (action === "recalculate") {
      await recalculateRecord(rec.id, review).catch(() => {});
    }
    setReviewPromptRecord(null);
    setReviewPromptAction(null);
    await refreshRecords();
  };

  const handleManualEntrySubmit = async (payload) => {
    setManualEntrySubmitting(true);
    setError(null);
    try {
      // Reuse the current batch if one is active, otherwise create a new one
      let batchId = currentBatchId;
      if (!batchId) {
        const created = await createBatch();
        batchId = created.data.batch.id;
        setCurrentBatchId(batchId);
        setIntakeState("processing");
      }
      const res = await addManualEntry(batchId, payload);
      // Append the new record to the list
      const newRecord = res.data.record;
      setRecords((prev) => [newRecord, ...prev]);
      setIntakeState("completed");
      // Refresh batch summary so counters update
      const bres = await getBatch(batchId);
      setBatchSummary(bres.data.batch || null);
      setIntakeMetrics((m) => ({
        ...m,
        records_normalized: (m.records_normalized || 0) + 1,
        records_extracted: (m.records_extracted || 0) + 1,
      }));
      setManualEntryOpen(false);
      return true;
    } catch (e) {
      setError(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed to add manual entry");
      return false;
    } finally {
      setManualEntrySubmitting(false);
    }
  };
  const handleApproveAllSafe = async () => {
    // Bulk-approve all "safe" rows with a single review-intelligence
    // event per row. This keeps the learning data complete even on the
    // express path. We synthesise a sensible default (low_confidence +
    // high user_confidence = "the AI was low confidence, but I trust
    // it for these auto-safe rows").
    const safe = records.filter((r) => r.status === "safe");
    const bulkReview = {
      reason_code: "low_confidence",
      user_confidence: "high",
      reason_text: "Bulk approval of AI-safe rows",
    };
    for (const r of safe) {
      await approveRecord(r.id, bulkReview).catch(() => {});
    }
    await refreshRecords();
  };
  const handleEditSave = async (payload) => {
    if (!editItem) return;
    await editRecord(editItem.id, payload).catch((e) => { throw e; });
    await refreshRecords();
  };
  const handleAllocateSave = async (payload) => {
    if (!allocItem) return;
    await manualAllocateRecord(allocItem.id, payload).catch((e) => { throw e; });
    await refreshRecords();
  };
  const handleRecalculate = (recordId) => {
    // P3 — recalc also goes through the Review Intelligence prompt so
    // we capture WHY the user is asking for a recompute.
    const rec = records.find((r) => r.id === recordId) || { id: recordId };
    setReviewPromptRecord(rec);
    setReviewPromptAction("recalculate");
  };
  const handleCommit = async () => {
    if (!currentBatchId) return;
    setCommitting(true);
    try {
      await commitBatch(currentBatchId);
      await refreshRecords();
    } finally {
      setCommitting(false);
    }
  };
  const handleApply = async () => {
    if (!currentBatchId) return;
    setApplying(true);
    try {
      const res = await applyBatch(currentBatchId);
      setApplyResult(res.data);
      await refreshRecords();
    } finally {
      setApplying(false);
    }
  };
  const scrollToRecords = () => recordsRef.current?.scrollIntoView({ behavior: "smooth" });

  /* ── Derived counts (strict: batch-scoped only) ── */
  const counts = useMemo(() => {
    const c = { pending: 0, safe: 0, review: 0, approved: 0, committed: 0, not_analysed: 0 };
    for (const r of records) {
      if (r.status === "committed") c.committed++;
      else c.pending++;
      if (r.status === "safe") c.safe++;
      if (r.status === "needs_review") c.review++;
      if (r.status === "approved") c.approved++;
      if (r.status === "not_analysed") c.not_analysed++;
    }
    return c;
  }, [records]);

  const scopedRecords = useMemo(() => {
    if (currentScope === "committed") return records.filter((r) => r.status === "committed");
    if (currentScope === "review")    return records.filter((r) => r.status === "needs_review");
    if (currentScope === "pending")   return records.filter((r) => r.status !== "committed");
    return records;
  }, [records, currentScope]);

  const canRunDiagnostic = !!currentBatchId && counts.not_analysed > 0 && runStatus !== "running";
  const canCommit = counts.approved > 0 && !committing;
  const canApply = !applying && records.some((r) => r.status === "committed" && !r.applied);

  /* ═══════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════ */
  return (
    <div className="space-y-6" data-testid="datapool-page-v1">

      {/* 1. PAGE HEADER */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-slate-50" data-testid="datapool-title">AI Data Pool</h1>
          <p className="text-sm text-slate-500 mt-0.5">Upload, analyse, and commit financial data into truth</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={handleRunDiagnostic} disabled={!canRunDiagnostic}
            className="bg-cyan-600 hover:bg-cyan-500 text-white gap-2 font-semibold" data-testid="header-run-diagnostic-btn">
            {runStatus === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <BrainCircuit className="w-4 h-4" />}
            Run AI Diagnostic
          </Button>
          <Button onClick={handleCommit} disabled={!canCommit} variant="outline"
            className="border-slate-700 text-slate-300 gap-2 font-semibold" data-testid="header-commit-btn">
            {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Commit to Financial Truth
          </Button>
        </div>
      </div>

      {/* 2. UPLOAD PANEL + MANUAL ENTRY */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-stretch">
        <UploadPanel loading={loading} onFiles={handleUpload} />
        <button
          onClick={() => setManualEntryOpen(true)}
          className="rounded-xl border border-cyan-500/25 bg-[#0a1628] hover:bg-[#0d1d33] p-5 flex flex-col items-center justify-center gap-2 min-w-[200px] transition-colors group"
          data-testid="open-manual-entry-btn">
          <PenLine className="w-7 h-7 text-cyan-400 group-hover:scale-110 transition-transform" />
          <span className="text-sm font-barlow font-semibold text-slate-200">Add Manual Entry</span>
          <span className="text-[10px] text-slate-500 text-center max-w-[180px] leading-tight">
            Loans, cash payments, off-bank transactions
          </span>
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/[0.04] p-3 flex items-center gap-3" data-testid="datapool-error">
          <XCircle className="w-4 h-4 text-red-400" />
          <span className="text-xs text-red-400">{error}</span>
          <Button onClick={() => setError(null)} variant="ghost" size="sm" className="ml-auto text-xs text-slate-500">Dismiss</Button>
        </div>
      )}

      {/* 3. ACTIVE BATCH BANNER */}
      {batchSummary && (
        <BatchSummary batchId={currentBatchId} summary={batchSummary} fileResults={fileResults} />
      )}

      {/* 3a. UPLOAD STATUS TRACKER — shows the async staged pipeline's
          progress in real-time: parsing → normalising → classifying →
          allocating → applying → reporting. Polls /status every 1.5s
          and surfaces the final result (applied / review / failed). */}
      {currentBatchId && (
        <UploadStatusTracker
          batchId={currentBatchId}
          onComplete={async () => {
            if (currentBatchId) {
              await refreshRecords(currentBatchId);
              try {
                const bres = await getBatch(currentBatchId);
                setBatchSummary(bres.data.batch);
              } catch (_e) { /* non-fatal */ }
            }
          }}
        />
      )}

      {/* 3b. INTAKE SUMMARY — stage A: parse + normalize result */}
      {(intakeState === "uploading" || intakeState === "processing" ||
        intakeState === "completed" || intakeState === "completed_with_issues" ||
        intakeState === "review_required" || intakeState === "failed") && (
        <IntakeSummaryPanel
          intakeState={intakeState}
          filesAttempted={intakeMetrics.files_attempted}
          filesSucceeded={intakeMetrics.files_succeeded}
          filesFailed={intakeMetrics.files_failed}
          recordsExtracted={intakeMetrics.records_extracted}
          recordsNormalized={intakeMetrics.records_normalized}
          recordsFlaggedForReview={intakeMetrics.records_flagged_for_review}
          pipelineMessage={pipelineMessage}
          fileErrors={fileErrors}
        />
      )}

      {/* 3b2. INTAKE STAGES — the 6-stage pipeline progression */}
      {(intakeState === "uploading" || intakeState === "processing" ||
        intakeState === "completed" || intakeState === "completed_with_issues" ||
        intakeState === "failed") && (
        <IntakeStagesPanel
          intakeState={intakeState}
          filesAttempted={intakeMetrics.files_attempted}
          filesSucceeded={intakeMetrics.files_succeeded}
          filesFailed={intakeMetrics.files_failed}
          recordsExtracted={intakeMetrics.records_extracted}
          recordsNormalized={intakeMetrics.records_normalized}
          recordsFlaggedForReview={intakeMetrics.records_flagged_for_review}
          fileResults={fileResults}
        />
      )}

      {/* 3c. FILE RESULTS LIST — per-file intake outcomes */}
      {fileResults.length > 0 && (intakeState === "completed" || intakeState === "completed_with_issues" || intakeState === "failed" || intakeState === "review_required") && (
        <FileResultsList files={fileResults} />
      )}

      {/* 3d. READY FOR DIAGNOSTIC BANNER — appears when intake is done + records are pooled + no diagnostic run yet */}
      {currentBatchId && intakeState !== "uploading" && intakeState !== "processing" &&
       intakeMetrics.records_normalized > 0 && runStatus === "idle" && !runSummary && (
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] p-4 flex items-center gap-3" data-testid="ready-for-diagnostic-banner">
          <Activity className="w-5 h-5 text-cyan-400 shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-cyan-400 font-semibold" data-testid="ready-title">Records Ready for AI Diagnostic</p>
            <p className="text-xs text-slate-400">
              The system has prepared <span className="font-mono text-cyan-300">{intakeMetrics.records_normalized}</span> normalized record{intakeMetrics.records_normalized !== 1 ? "s" : ""} for financial diagnosis.
            </p>
          </div>
          <Button onClick={handleRunDiagnostic} className="bg-cyan-600 hover:bg-cyan-500 gap-2" data-testid="ready-run-diagnostic-btn">
            <BrainCircuit className="w-4 h-4" /> Run AI Diagnostic
          </Button>
        </div>
      )}

      {/* Empty state */}
      {!currentBatchId && !loading && (
        <Card className="bg-slate-900/20 border-slate-800/30 border-dashed" data-testid="empty-state">
          <CardContent className="p-12 text-center">
            <BrainCircuit className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-sm text-slate-300 font-semibold mb-1">No active batch yet</p>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Upload financial files above to start a new working batch. This workspace never mixes historical data with the current upload.
            </p>
          </CardContent>
        </Card>
      )}

      {/* 4. STATUS CARDS — batch-scoped */}
      {currentBatchId && records.length > 0 && <StatusCards counts={counts} />}

      {/* 5. SCOPE TABS */}
      {currentBatchId && records.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" ref={recordsRef}>
          {[
            { key: "pending",   label: `Pending (${counts.pending})` },
            { key: "review",    label: `⚠ Needs Review (${counts.review})`, highlight: counts.review > 0 },
            { key: "committed", label: `Committed (${counts.committed})` },
            { key: "all",       label: `All (${records.length})` },
          ].map((t) => (
            <button key={t.key} onClick={() => setCurrentScope(t.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                currentScope === t.key ? "bg-slate-800 text-slate-200" :
                t.highlight ? "bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/20" :
                "text-slate-500 hover:text-slate-300"
              }`}
              data-testid={`scope-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* 6. DIAGNOSTIC PANEL */}
      {runStatus === "running" && (
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] p-12" data-testid="diagnostic-running">
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
              <BrainCircuit className="w-7 h-7 text-cyan-400 animate-pulse" />
            </div>
            <h3 className="text-base font-barlow font-bold text-cyan-400">AI Diagnostic in Progress</h3>
            <p className="text-xs text-slate-400 mt-1">Processing {counts.not_analysed} record{counts.not_analysed !== 1 ? "s" : ""}...</p>
            <Loader2 className="w-5 h-5 text-cyan-500/50 animate-spin mt-3" />
          </div>
        </div>
      )}
      {runStatus !== "running" && runSummary && (
        <DiagnosticPanel
          run={runSummary}
          canApproveSafe={counts.safe > 0}
          canReview={counts.review > 0}
          onApproveAllSafe={handleApproveAllSafe}
          onReviewUncertain={() => { setCurrentScope("pending"); scrollToRecords(); }}
          onRetry={handleRunDiagnostic}
        />
      )}

      {/* 7. APPLY RESULT */}
      {applyResult && <ApplyResultPanel result={applyResult} onDismiss={() => setApplyResult(null)} onGoToDashboard={() => navigate("/dashboard")} />}

      {/* 8. RECORD LIST */}
      {currentBatchId && scopedRecords.length > 0 && (
        <div className="space-y-3" data-testid="record-list">
          {scopedRecords.map((r) => (
            <RecordCard key={r.id} record={r}
              onEdit={setEditItem}
              onApprove={handleApprove}
              onAllocate={setAllocItem}
              onRecalculate={handleRecalculate}
              onHistory={setHistoryItemId}
              formatMoney={formatMoney} />
          ))}
        </div>
      )}
      {currentBatchId && records.length > 0 && scopedRecords.length === 0 && (
        <div className="text-center py-8 text-xs text-slate-500" data-testid="empty-scope">
          {currentScope === "committed" ? "No committed records in this batch yet." : "No pending records."}
        </div>
      )}

      {/* 9. COMMIT / APPLY PANEL — final stage */}
      {currentBatchId && (counts.approved > 0 || counts.committed > 0) && (
        <div className="rounded-xl border border-slate-800/40 bg-slate-900/30 p-5" data-testid="commit-apply-panel">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-800/60 flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5 text-slate-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-barlow font-bold text-slate-200">Commit and Apply Financial Truth</h3>
              <p className="text-xs text-slate-500 mt-1">
                Approved records can now be committed into the ledger-backed truth layer and applied to the dashboard.
              </p>
              <p className="text-[10px] text-slate-600 italic mt-2">
                The system never applies financial truth automatically. Records must be approved and committed before dashboard application.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button onClick={handleCommit} disabled={!canCommit} variant="outline"
                className="border-slate-700 text-slate-300 gap-2 font-semibold" data-testid="panel-commit-btn">
                {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Commit ({counts.approved})
              </Button>
              <Button onClick={handleApply} disabled={!canApply}
                className="bg-amber-600 hover:bg-amber-500 text-slate-900 gap-2 font-semibold" data-testid="panel-apply-btn">
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                Apply to Dashboard ({counts.committed})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-diagnostic prompt (only after a failed/partial diagnostic when more records still need analysis) */}
      {currentBatchId && counts.not_analysed > 0 && runStatus === "idle" && runSummary && (
        <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/[0.03] flex items-center gap-3" data-testid="run-diagnostic-prompt">
          <Activity className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <p className="text-sm text-amber-400 font-semibold">Some records still need analysis</p>
            <p className="text-xs text-slate-500">
              {counts.not_analysed} record{counts.not_analysed !== 1 ? "s" : ""} not yet analysed. Run AI Diagnostic to continue.
            </p>
          </div>
          <Button onClick={handleRunDiagnostic} className="ml-auto bg-cyan-600 hover:bg-cyan-500 gap-2" data-testid="prompt-run-diagnostic-btn">
            <BrainCircuit className="w-4 h-4" /> Run AI Diagnostic
          </Button>
        </div>
      )}

      {/* Org-wide pending banner — visible whenever records are sitting unanalysed
          across one or more historical batches. Two paths:
          • "Auto-Allocate" — instant, no AI, deterministic rules only
          • "Analyse with AI" — slower, calls GPT-5.2 for ambiguous records */}
      {allPendingCount > 0 && (
        <div className="p-4 rounded-xl border border-cyan-500/30 bg-gradient-to-r from-cyan-500/[0.05] to-transparent flex items-center gap-3 flex-wrap"
             data-testid="all-pending-banner">
          <BrainCircuit className="w-5 h-5 text-cyan-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-cyan-300 font-semibold" data-testid="all-pending-headline">
              {allPendingCount} record{allPendingCount !== 1 ? "s" : ""} across {allPendingBatches} batch{allPendingBatches !== 1 ? "es" : ""} need{allPendingCount === 1 ? "s" : ""} analysis
            </p>
            <p className="text-xs text-slate-500">
              Auto-Allocate is instant and never depends on AI. Analyse with AI is slower but uses GPT-5.2 for ambiguous descriptions.
            </p>
          </div>
          <Button onClick={handleOpenPreview} disabled={rulesOnlyRunning || sweepRunning}
            className="bg-emerald-600 hover:bg-emerald-500 gap-2" data-testid="auto-allocate-btn">
            {rulesOnlyRunning ? (<><Activity className="w-4 h-4 animate-spin" /> Allocating…</>)
                              : (<><Zap className="w-4 h-4" /> Auto-Allocate (No AI)</>)}
          </Button>
          <Button onClick={handleRunAllPending} disabled={sweepRunning || rulesOnlyRunning}
            variant="outline"
            className="border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 gap-2" data-testid="run-all-pending-btn">
            {sweepRunning ? (<><Activity className="w-4 h-4 animate-spin" /> Analysing…</>)
                          : (<><BrainCircuit className="w-4 h-4" /> Analyse with AI</>)}
          </Button>
        </div>
      )}

      {/* 10. DOCUMENT ARCHIVE — always visible, never affected by current-session state */}
      <DocumentArchive
        onSelectBatch={async (batchId) => {
          setCurrentBatchId(batchId);
          setRunSummary(null);
          setRunStatus("idle");
          setApplyResult(null);
          setCurrentScope("all");
          try {
            const [b, r] = await Promise.all([getBatch(batchId), getArchivedRecords(batchId)]);
            setBatchSummary(b.data.batch || null);
            const list = r.data.records;
            if (Array.isArray(list)) setRecords(list);
          } catch { /* leave records intact on error */ }
        }}
      />

      {/* Modals */}
      <ManualEntryModal
        open={manualEntryOpen}
        onOpenChange={setManualEntryOpen}
        onSubmit={handleManualEntrySubmit}
        submitting={manualEntrySubmitting}
      />
      <EditRecordModal open={!!editItem} record={editItem} onClose={() => setEditItem(null)} onSave={handleEditSave} />
      <ManualAllocateModal open={!!allocItem} record={allocItem} onClose={() => setAllocItem(null)} onSave={handleAllocateSave} />
      <HistoryDrawer open={!!historyItemId} recordId={historyItemId} onClose={() => setHistoryItemId(null)} />

      {/* P3 — Review Intelligence prompt for approve / recalculate */}
      <ReviewIntelligencePrompt
        open={!!reviewPromptAction}
        action={reviewPromptAction}
        record={reviewPromptRecord}
        onClose={() => {
          setReviewPromptAction(null);
          setReviewPromptRecord(null);
        }}
        onConfirm={handleReviewConfirm}
      />

      {/* Dry-run preview before any allocator write */}
      <PreviewSweepModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onConfirm={handleConfirmAutoAllocate}
      />

      {/* Floating rollback toast — visible after a sweep has just run.
          Gives the user one click to undo the entire bulk allocation. */}
      {lastSweep && (
        <div
          className="fixed bottom-6 right-6 z-50 max-w-sm p-4 rounded-xl border border-emerald-500/30 bg-slate-950/95 backdrop-blur shadow-2xl shadow-emerald-500/10"
          data-testid="rollback-toast"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-emerald-300">
                Allocated {lastSweep.allocated} records
              </p>
              {lastSweep.review > 0 && (
                <p className="text-[11px] text-amber-300 mt-0.5">
                  {lastSweep.review} flagged for review — see badge in nav
                </p>
              )}
              <p className="text-[10px] text-slate-500 mt-1">
                You can undo this for the next 30 minutes.
              </p>
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRollbackLastSweep}
                  disabled={rulesOnlyRunning}
                  className="h-7 text-[11px] border-slate-700 text-slate-300"
                  data-testid="rollback-btn"
                >
                  Undo allocation
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLastSweep(null)}
                  className="h-7 text-[11px] text-slate-500"
                  data-testid="rollback-dismiss-btn"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
